import { useCallback, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import {
  DEFAULT_COMPANION_FIELDS,
  DEFAULT_PRIMARY_GUEST_FIELDS,
  getGuestAddressMode,
  normalizeGuestAddressFieldModes,
  type CompanionFieldsConfig,
} from "@/lib/companionFields";
import {
  DEFAULT_ONLINE_BOOKING_FLOW,
  DEFAULT_ONLINE_BOOKING_MIN_STAY,
  DEFAULT_ONLINE_BOOKING_SAME_DAY_CUTOFF,
  DEFAULT_ONLINE_BOOKING_UPSELLING,
  type IntegrationsSettings,
  type OnlineBookingAddonArticle,
  type OnlineBookingFlowSettings,
  type OnlineBookingMinStaySettings,
  type OnlineBookingMode,
  type OnlineBookingSameDayCutoffSettings,
  type OnlineBookingSeasonPeriod,
  type OnlineBookingUpsellingSettings,
  type OnlineBookingUpsellingTemplateSuggestions,
} from "@/lib/onlineBooking";
import type { Article } from "@/types/camping";

interface TenantSettings {
  integrations: IntegrationsSettings;
}

export function useOnlineBookingSettings(canWrite: boolean) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const [publicBookingEnabled, setPublicBookingEnabled] = useState(true);
  const [onlineBookingMode, setOnlineBookingMode] = useState<OnlineBookingMode>("inquiry");
  const [onlineBookingAvailabilityCheck, setOnlineBookingAvailabilityCheck] = useState(true);
  const [touristIntegrationEnabled, setTouristIntegrationEnabled] = useState(false);
  const [autoSendConfirmationEmail, setAutoSendConfirmationEmail] = useState(true);
  const [companionFieldsAdult, setCompanionFieldsAdult] = useState<CompanionFieldsConfig>(
    DEFAULT_COMPANION_FIELDS,
  );
  const [companionFieldsChild, setCompanionFieldsChild] = useState<CompanionFieldsConfig>(
    DEFAULT_COMPANION_FIELDS,
  );
  const [primaryGuestFields, setPrimaryGuestFields] = useState<CompanionFieldsConfig>(
    DEFAULT_PRIMARY_GUEST_FIELDS,
  );
  const [flow, setFlow] = useState<OnlineBookingFlowSettings>(DEFAULT_ONLINE_BOOKING_FLOW);
  const [upselling, setUpselling] = useState<OnlineBookingUpsellingSettings>(
    DEFAULT_ONLINE_BOOKING_UPSELLING,
  );
  const [addons, setAddons] = useState<OnlineBookingAddonArticle[]>([]);
  const [seasons, setSeasons] = useState<OnlineBookingSeasonPeriod[]>([]);
  const [minStay, setMinStay] = useState<OnlineBookingMinStaySettings>(
    DEFAULT_ONLINE_BOOKING_MIN_STAY,
  );
  const [sameDayCutoff, setSameDayCutoff] = useState<OnlineBookingSameDayCutoffSettings>(
    DEFAULT_ONLINE_BOOKING_SAME_DAY_CUTOFF,
  );
  const [articles, setArticles] = useState<Article[]>([]);

  const loadUpsellingTemplateSuggestions = useCallback(async () => {
    if (!canWrite) return;
    try {
      const suggestions = await api.get<OnlineBookingUpsellingTemplateSuggestions>(
        "/api/v1/settings/integrations/online-booking-marketing-suggestions",
      );
      setUpselling((current) => ({
        ...current,
        template_upgrade: suggestions.template_upgrade,
        template_cheaper: suggestions.template_cheaper,
        template_total_difference: suggestions.template_total_difference,
        template_upgrade_en: suggestions.template_upgrade_en ?? "",
        template_cheaper_en: suggestions.template_cheaper_en ?? "",
        template_total_difference_en: suggestions.template_total_difference_en ?? "",
      }));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Vorschläge konnten nicht geladen werden.");
    }
  }, [canWrite]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<TenantSettings>("/api/v1/settings"),
      api.get<Article[]>("/api/v1/articles?include_inactive=false"),
    ])
      .then(([settings, articleList]) => {
        const integ = settings.integrations;
        setPublicBookingEnabled(integ?.public_booking_enabled ?? true);
        setOnlineBookingMode(integ?.online_booking_mode ?? "inquiry");
        setOnlineBookingAvailabilityCheck(integ?.online_booking_availability_check ?? true);
        setTouristIntegrationEnabled(integ?.tourist_integration?.enabled ?? false);
        setAutoSendConfirmationEmail(integ?.auto_send_confirmation_email ?? true);
        const legacyCompanionFields = integ?.companion_fields;
        setCompanionFieldsAdult(
          normalizeGuestAddressFieldModes({
            ...DEFAULT_COMPANION_FIELDS,
            ...(integ?.companion_fields_adult ?? legacyCompanionFields),
          }),
        );
        setCompanionFieldsChild(
          normalizeGuestAddressFieldModes({
            ...DEFAULT_COMPANION_FIELDS,
            ...(integ?.companion_fields_child ?? legacyCompanionFields),
          }),
        );
        const flowLoaded = { ...DEFAULT_ONLINE_BOOKING_FLOW, ...integ?.online_booking_flow };
        const loadedPrimaryGuestFields: CompanionFieldsConfig = normalizeGuestAddressFieldModes({
          ...DEFAULT_PRIMARY_GUEST_FIELDS,
          ...integ?.primary_guest_fields,
          ...(integ?.primary_guest_fields
            ? {}
            : flowLoaded.require_guest_address
              ? {
                  street: "required" as const,
                  postal_code: "required" as const,
                  city: "required" as const,
                  country_code: "optional" as const,
                }
              : {}),
        });
        setPrimaryGuestFields(loadedPrimaryGuestFields);
        setFlow(flowLoaded);
        setUpselling({
          ...DEFAULT_ONLINE_BOOKING_UPSELLING,
          ...integ?.online_booking_upselling,
        });
        setAddons(integ?.online_booking_addons ?? []);
        setSeasons(integ?.online_booking_seasons ?? []);
        setMinStay({
          ...DEFAULT_ONLINE_BOOKING_MIN_STAY,
          ...integ?.online_booking_min_stay,
          periods: integ?.online_booking_min_stay?.periods ?? [],
        });
        setSameDayCutoff({
          ...DEFAULT_ONLINE_BOOKING_SAME_DAY_CUTOFF,
          ...integ?.online_booking_same_day_cutoff,
        });
        setArticles(articleList);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const availableArticlesForAddon = useMemo(() => {
    const used = new Set(addons.map((a) => a.article_id));
    return articles.filter((a) => a.is_active && !used.has(a.id));
  }, [articles, addons]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.patch<TenantSettings>("/api/v1/settings/integrations", {
        public_booking_enabled: publicBookingEnabled,
        online_booking_mode: onlineBookingMode,
        online_booking_availability_check: onlineBookingAvailabilityCheck,
        auto_send_confirmation_email: autoSendConfirmationEmail,
        companion_fields_adult: companionFieldsAdult,
        companion_fields_child: companionFieldsChild,
        primary_guest_fields: primaryGuestFields,
        online_booking_flow: {
          ...flow,
          require_guest_address: getGuestAddressMode(primaryGuestFields) === "required",
        },
        online_booking_upselling: upselling,
        online_booking_addons: addons.map((a) => ({
          article_id: a.article_id,
          sort_order: a.sort_order,
          billing: a.billing,
          label: a.label,
          label_en: a.label_en ?? null,
        })),
        online_booking_seasons: seasons.map((s) => ({
          id: s.id,
          name: s.name.trim(),
          mode: s.mode,
          start_date: s.mode === "fixed" ? s.start_date : null,
          end_date: s.mode === "fixed" ? s.end_date : null,
          start_month: s.mode === "recurring" ? s.start_month : null,
          start_day: s.mode === "recurring" ? s.start_day : null,
          end_month: s.mode === "recurring" ? s.end_month : null,
          end_day: s.mode === "recurring" ? s.end_day : null,
        })),
        online_booking_min_stay: {
          enabled: minStay.enabled,
          default_min_nights: Math.max(1, minStay.default_min_nights),
          periods: minStay.periods.map((p) => ({
            id: p.id,
            name: p.name.trim(),
            min_nights: Math.max(1, p.min_nights),
            mode: p.mode,
            start_date: p.mode === "fixed" ? p.start_date : null,
            end_date: p.mode === "fixed" ? p.end_date : null,
            start_month: p.mode === "recurring" ? p.start_month : null,
            start_day: p.mode === "recurring" ? p.start_day : null,
            end_month: p.mode === "recurring" ? p.end_month : null,
            end_day: p.mode === "recurring" ? p.end_day : null,
          })),
        },
        online_booking_same_day_cutoff: {
          enabled: sameDayCutoff.enabled,
          cutoff_time: sameDayCutoff.cutoff_time,
        },
      });
      setSuccess(true);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return {
    loading,
    error,
    setError,
    success,
    saving,
    publicBookingEnabled,
    setPublicBookingEnabled,
    onlineBookingMode,
    setOnlineBookingMode,
    onlineBookingAvailabilityCheck,
    setOnlineBookingAvailabilityCheck,
    touristIntegrationEnabled,
    autoSendConfirmationEmail,
    setAutoSendConfirmationEmail,
    companionFieldsAdult,
    setCompanionFieldsAdult,
    companionFieldsChild,
    setCompanionFieldsChild,
    primaryGuestFields,
    setPrimaryGuestFields,
    flow,
    setFlow,
    upselling,
    setUpselling,
    addons,
    setAddons,
    seasons,
    setSeasons,
    minStay,
    setMinStay,
    sameDayCutoff,
    setSameDayCutoff,
    articles,
    availableArticlesForAddon,
    load,
    loadUpsellingTemplateSuggestions,
    onSave,
  };
}

export type OnlineBookingSettings = ReturnType<typeof useOnlineBookingSettings>;
